/**
 * Phase 13 — Team management page (top-level, mounts at `/team`).
 *
 * Redesigned from the Claude Design "Team.html" mockup. The design frames Team
 * as identity + Role (what a teammate can DO) + Scope (what they can TOUCH —
 * brands). Resources themselves are connected in Control Center; here you only
 * choose which a teammate may work in.
 *
 * Adapted to the real backend: 5 roles (owner/team_lead/manager/analyst/
 * read_only), brand (shop) access with per-shop permission levels, and groups.
 * Per-person Tool/MCP grants from the mockup are deferred (governed per brand in
 * Control Center) and surfaced as an inline note rather than editable toggles.
 *
 * UI stays in the legacy SCSS style (no MUI). Two slide-over drawers (Invite,
 * Manage access) replace the previous centered modals for the primary editing
 * surfaces; confirmation/result dialogs keep the shared `Popup`.
 *
 * All CRUD operations are preserved: invite, list, update (name/role/lead/
 * active), shop assign/revoke, delete, group create/add-member/remove-member.
 * RBAC gates (`team.view` / `team.invite` / `team.delete`) carry over.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/team/team.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import Popup from '../../ui/popup/Popup';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import AINormalInput from '../../ui/inputs/AINormalInput';
import AISelectInput from '../../ui/inputs/AISelectInput';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';

import {
  plusSignSVG,
  trashSVG,
  sendSVG,
  copySVG,
  keySVG,
  twoUsersSVG,
} from '../../lib/allCustomSVGs';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import {
  AgencyEmployee,
  AgencyGroup,
  CreateEmployeeResponse,
  EmployeeInvitation,
  addGroupMember,
  assignShopToEmployee,
  createEmployee,
  createGroup,
  deleteGroup,
  updateGroup,
  deleteEmployee,
  inviteEmployee,
  listEmployeesWithPlatforms,
  listGroups,
  removeGroupMember,
  resendEmployeeInvitation,
  revokeShopFromEmployee,
  updateEmployee,
} from '../../lib/api/identity/identity-api';
import {
  AgencyRole,
  McpAccess,
  McpAccessMode,
  PlatformDef,
  listRoles,
} from '../../lib/api/roles/roles-api';
import type { AgencyAccessibleShop } from '../../store/reducers/authSlice';
import {
  RoleGate,
  canResolve,
  canViewTeamPage,
  useCapabilities,
  useEmployeeRole,
} from '../../lib/rbac';
// Client management (invite whitelabel-portal clients) was relocated here from
// the Reporting surface (product-owner decision). It reuses the existing
// `portal.clients.manage` capability — no new role — and is scoped per brand.
import ManageClientsPanel from '../client-reports/clients/ManageClientsPanel';
import TeamActivityPanel from './TeamActivityPanel';
import { showTeamActivityTab } from './team-activity-helpers';
import {
  assignableRoles,
  canManageMember,
  isOnlyOwner,
  teamCounts,
  teamErrorText,
} from './team-rank';
import { memberFormErrors, memberFormValid } from './member-form';
import SecretValue from './SecretValue';
import {
  addMemberRequest,
  defaultBrandLevel,
  followRoleDefault,
  inviteMemberRequest,
  drawerRefusalFrom,
  pendingInvitationFrom,
  type DrawerRefusal,
  type PendingInvitation,
} from './brand-level';
import PendingInvitationNotice from './PendingInvitationNotice';
import {
  canDeleteTeam,
  teamDialogErrorText,
  teamFormReady,
  teamLeadName,
} from './team-groups';

// ============================================================================
//  Module-level constants & pure helpers
// ============================================================================

// The 5 built-in system role keys. NOTE: these are no longer the source of
// truth for the role *selectors* — those are populated live from
// GET /agency/roles (system + assignable custom roles). This list is retained
// only as (a) the tone/blurb lookup for the known system roles and (b) a
// fallback if the live roles fetch fails.
const ROLE_OPTIONS = [
  'owner',
  'manager',
  'team_lead',
  'analyst',
  'read_only',
] as const;
// The BACKEND vocabulary for employee_shop_permissions.permission_level
// (read|propose|approve). The UI labels these Manager/Analyst/Read-only, but
// the wire value must be canonical — sending the label vocabulary made every
// brand assignment fail validation ("The selected permission level is
// invalid.").
type ShopPermission = 'approve' | 'propose' | 'read';

// A role as offered in a selector: the stable `key` (sent to the backend) plus
// the human `name` and an optional `description` (used as the blurb for custom
// roles that have no static i18n blurb).
interface RoleOption {
  key: string;
  name: string;
  description?: string;
  // The role's own MCP platform allowlist, so the member drawer can show the
  // EFFECTIVE set (role ∩ member) and warn when the two produce nothing.
  mcpAccess?: McpAccess;
  // The role's capability keys when the roles list is loaded; the default
  // brand access level of a new member follows them.
  capabilities?: string[];
}

// Role → badge tone (mapped to the design's pastel palette in team.scss).
const ROLE_TONE: Record<string, string> = {
  owner: 'violet',
  team_lead: 'blue',
  manager: 'amber',
  analyst: 'green',
  read_only: 'gray',
};

// Roles that count as approvers for the "Can approve" stat tile.
const APPROVER_ROLES: Record<string, boolean> = {
  owner: true,
  team_lead: true,
  manager: true,
};

// Roles whose brand scope is implicit (full access) and therefore not editable.
const SCOPE_LOCKED_ROLES: Record<string, boolean> = { owner: true };

// Deterministic group color from its id (groups carry no color in the backend).
const GROUP_COLORS = [
  '#2A6FDB',
  '#7A5AE0',
  '#1F8A5B',
  '#C2410C',
  '#0E7490',
  '#B45309',
];
const groupColor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GROUP_COLORS[h % GROUP_COLORS.length];
};

const initialsOf = (name?: string, email?: string): string => {
  const src = (name && name.trim()) || (email ? email.split('@')[0] : '');
  if (!src) return '?';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  const chars =
    parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2);
  return chars.toUpperCase();
};

// Normalise a stored permission_level to the canonical vocabulary, tolerating
// the legacy label vocabulary (analyst/read_only/manager) that older rows or
// bundles may still carry.
const normPerm = (p?: string): ShopPermission =>
  p === 'propose' || p === 'analyst'
    ? 'propose'
    : p === 'read' || p === 'read_only'
    ? 'read'
    : 'approve';

// Stable string form of a brand→permission map for dirty-checking.
const serializePerms = (perms: Record<string, string>): string =>
  Object.keys(perms)
    .sort()
    .map((k) => `${k}:${perms[k]}`)
    .join('|');

// ---- Per-member MCP platform allowlist helpers ----------------------------
// Mirror the SettingsRoles editor so the member drawer and the role editor
// speak the exact same wire shape. A missing/`all` value = no restriction.

// Normalise a possibly-absent mcp_access to a concrete { mode, platforms:Set }.
const normMcpAccess = (
  m?: McpAccess
): { mode: McpAccessMode; platforms: Set<string> } => {
  if (!m || m.mode === 'all') return { mode: 'all', platforms: new Set() };
  return { mode: 'allowlist', platforms: new Set(m.platforms ?? []) };
};

// Build the wire mcp_access from a draft mode + selected platform keys.
const mcpAccessWire = (
  mode: McpAccessMode,
  platforms: Set<string>
): McpAccess =>
  mode === 'all'
    ? { mode: 'all' }
    : { mode: 'allowlist', platforms: Array.from(platforms) };

const sameStrSet = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
};

// True when two draft allowlists are equivalent (mode + platform set).
const sameMcpAccess = (
  a: { mode: McpAccessMode; platforms: Set<string> },
  b: { mode: McpAccessMode; platforms: Set<string> }
): boolean => {
  if (a.mode !== b.mode) return false;
  if (a.mode === 'all') return true;
  return sameStrSet(Array.from(a.platforms), Array.from(b.platforms));
};

// Single-path inline icons (no @mui/icons-material in this repo).
const ICON_PATHS: Record<string, string> = {
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
  brand: 'M3 21h18M5 21V7l7-4 7 4v14M10 21v-5h4v5',
  x: 'M18 6 6 18M6 6l12 12',
  chevronRight: 'm9 18 6-6-6-6',
  lock: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14v9H5z',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 16v-4M12 8h.01',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7',
  trash:
    'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
};

const Icon: React.FC<{
  name: string;
  size?: number;
  stroke?: number;
  className?: string;
}> = ({ name, size = 16, stroke = 1.8, className }) => {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={stroke}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d={d} />
    </svg>
  );
};

// `role` is the stable key; `label` (when given) overrides the displayed text
// for custom roles whose key isn't a translatable system key. System keys still
// resolve through i18n; unknown keys fall back to the provided label or the key.
const RoleBadge: React.FC<{ role: string; label?: string }> = ({
  role,
  label,
}) => {
  const { t } = useTranslation();
  const tone = ROLE_TONE[role] || 'gray';
  const text = ROLE_TONE[role] ? t('Role_' + role) : label || role;
  return <span className={`roleBadge roleBadge--${tone}`}>{text}</span>;
};

const Avatar: React.FC<{ name?: string; email?: string; title?: string }> = ({
  name,
  email,
  title,
}) => (
  <span className='tavatar' title={title}>
    {initialsOf(name, email)}
  </span>
);

const Toggle: React.FC<{
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ on, onChange, disabled }) => (
  <button
    type='button'
    className={`teamToggle ${on ? 'teamToggle--on' : ''} ${
      disabled ? 'teamToggle--disabled' : ''
    }`}
    role='switch'
    aria-checked={on}
    disabled={disabled}
    onClick={() => !disabled && onChange(!on)}
  >
    <span className='teamToggle__knob' />
  </button>
);

// Shared role radio grid (Invite + Manage-access drawers). Options come live
// from GET /agency/roles (system + assignable custom roles). System roles keep
// their static i18n blurb; custom roles use their own description.
const RoleGrid: React.FC<{
  value: string;
  roles: RoleOption[];
  onChange: (r: string) => void;
}> = ({ value, roles, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className='roleGrid'>
      {roles.map((r) => {
        const isSystem = !!ROLE_TONE[r.key];
        const blurb = isSystem
          ? t('RoleBlurb_' + r.key)
          : r.description || '';
        return (
          <button
            type='button'
            key={r.key}
            data-testid={`role-option-${r.key}`}
            className={`roleOpt ${value === r.key ? 'roleOpt--active' : ''}`}
            onClick={() => onChange(r.key)}
          >
            <span className='roleOpt__radio' />
            <span className='roleOpt__body'>
              <span className='roleOpt__name'>
                <RoleBadge role={r.key} label={r.name} />
              </span>
              {blurb && <span className='roleOpt__blurb'>{blurb}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const OwnerFullPanel: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className='scopeLocked'>
      <Icon name='lock' size={18} />
      <span>
        <span className='scopeLocked__title'>{t('TeamOwnerFullTitle')}</span>
        <span className='scopeLocked__body'>{t('TeamOwnerFullBody')}</span>
      </span>
    </div>
  );
};

// ============================================================================
//  Platform (MCP) access section — shared by the Manage-access + Invite drawers
// ============================================================================

interface PlatformAccessSectionProps {
  allPlatforms: PlatformDef[];
  mode: McpAccessMode;
  platforms: Set<string>;
  // The member's effective role allowlist (if the role itself is restricted),
  // used only to show the effective set + warn on an empty intersection.
  roleMcpAccess?: McpAccess;
  onModeChange: (mode: McpAccessMode) => void;
  onTogglePlatform: (key: string) => void;
}

/**
 * Per-MEMBER platform (MCP connector) access, in the Team page's SCSS house
 * style (NOT the MUI editor SettingsRoles uses — TeamPage is MUI-free). A
 * toggle flips between "All platforms" (inherit the role) and "Selected
 * platforms" (an allowlist checked below). Governs which platforms this
 * member's AI tools reach on EVERY brand — not what they can see/connect in the
 * portal. When the role is ALSO restricted, the effective set is the
 * intersection; an empty intersection is surfaced as a warning.
 */
const PlatformAccessSection: React.FC<PlatformAccessSectionProps> = ({
  allPlatforms,
  mode,
  platforms,
  roleMcpAccess,
  onModeChange,
  onTogglePlatform,
}) => {
  const { t } = useTranslation();
  const limited = mode === 'allowlist';

  // Role allowlist as a Set (empty Set = role restricts to nothing; undefined =
  // role imposes no restriction).
  const roleAllow =
    roleMcpAccess && roleMcpAccess.mode === 'allowlist'
      ? new Set(roleMcpAccess.platforms ?? [])
      : null;

  // Effective platform keys this member can reach = (member allowlist or all) ∩
  // (role allowlist or all), further limited to platforms that exist.
  const effectiveKeys = allPlatforms
    .map((p) => p.key)
    .filter((k) => (limited ? platforms.has(k) : true))
    .filter((k) => (roleAllow ? roleAllow.has(k) : true));

  const emptyIntersection = limited && effectiveKeys.length === 0;

  return (
    <div className='dsec'>
      <div className='dsec__label'>
        {t('PlatformAccess')}
        <span className='dsec__hint'>{t('PlatformAccessHint')}</span>
      </div>

      <div className='scopeHead'>
        <span className='dsec__hint'>
          {!limited
            ? t('PlatformAccessAll')
            : t('PlatformAccessCount', { count: platforms.size })}
        </span>
        <span className='platformAccessToggle'>
          <span className='dsec__hint'>{t('PlatformAccessLimit')}</span>
          <Toggle
            on={limited}
            onChange={(v) => onModeChange(v ? 'allowlist' : 'all')}
          />
        </span>
      </div>

      {limited && (
        <>
          {allPlatforms.length === 0 ? (
            <div className='scopeFoot'>{t('PlatformAccessNone')}</div>
          ) : (
            <div className='scopeList'>
              {allPlatforms.map((p) => {
                const on = platforms.has(p.key);
                // A platform the ROLE already forbids can still be ticked, but
                // it will never take effect — flag it so the admin isn't misled.
                const blockedByRole = !!roleAllow && !roleAllow.has(p.key);
                return (
                  <div
                    key={p.key}
                    className={`scopeRow ${on ? 'scopeRow--on' : ''}`}
                  >
                    <span className='scopeRow__left'>
                      <span className='scopeRow__name'>{p.label}</span>
                      {blockedByRole && (
                        <span className='scopeRow__sub'>
                          {t('PlatformAccessBlockedByRole')}
                        </span>
                      )}
                    </span>
                    <span className='scopeRow__right'>
                      <Toggle on={on} onChange={() => onTogglePlatform(p.key)} />
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {emptyIntersection && (
            <div className='scopeNote scopeNote--warn'>
              <Icon name='info' size={15} />
              <span>{t('PlatformAccessEmptyWarn')}</span>
            </div>
          )}
        </>
      )}

      <div className='scopeNote'>
        <Icon name='info' size={15} />
        <span>{t('PlatformAccessFoot')}</span>
      </div>
    </div>
  );
};

// ============================================================================
//  Manage-access drawer (role + brand scope for an existing member)
// ============================================================================

interface AccessSavePayload {
  name: string;
  role: string;
  is_team_lead: boolean;
  active: boolean;
  // null = role is scope-locked (owner); skip the brand diff entirely.
  brandPerms: Record<string, ShopPermission> | null;
  // Per-member platform allowlist, or null when unchanged (skip the field).
  mcpAccess: McpAccess | null;
}

interface AccessDrawerProps {
  member: AgencyEmployee;
  accessibleShops: AgencyAccessibleShop[];
  roleOptions: RoleOption[];
  allPlatforms: PlatformDef[];
  saving: boolean;
  // The member is the organisation's only owner: the role and the active
  // switch cannot change.
  onlyOwner?: boolean;
  onClose: () => void;
  onSave: (member: AgencyEmployee, payload: AccessSavePayload) => void;
}

const AccessDrawer: React.FC<AccessDrawerProps> = ({
  member,
  accessibleShops,
  roleOptions,
  allPlatforms,
  saving,
  onlyOwner,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(member.name || '');
  const [role, setRole] = useState<string>(member.role);
  const [isLead, setIsLead] = useState<boolean>(!!member.is_team_lead);
  const [active, setActive] = useState<boolean>(member.active !== false);

  // Per-member platform allowlist, seeded from the member's resolved mcp_access.
  const initialMcp = useMemo(
    () => normMcpAccess(member.mcp_access),
    [member.mcp_access]
  );
  const [mcpMode, setMcpMode] = useState<McpAccessMode>(initialMcp.mode);
  const [mcpPlatforms, setMcpPlatforms] = useState<Set<string>>(
    initialMcp.platforms
  );

  const toggleMcpPlatform = (key: string) =>
    setMcpPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Slugs the current user is allowed to grant/revoke.
  const editableSlugs = useMemo(() => {
    const map: Record<string, boolean> = {};
    accessibleShops.forEach((s) => {
      map[s.slug] = true;
    });
    return map;
  }, [accessibleShops]);

  // Seed the editable brand→permission map from the member's current grants,
  // limited to slugs the current user can see/change.
  const initialBrandPerms = useMemo(() => {
    const map: Record<string, ShopPermission> = {};
    (member.assigned_shops ?? []).forEach((s) => {
      if (editableSlugs[s.slug]) map[s.slug] = normPerm(s.permission_level);
    });
    return map;
  }, [member.assigned_shops, editableSlugs]);

  const [brandPerms, setBrandPerms] =
    useState<Record<string, ShopPermission>>(initialBrandPerms);

  // Ensure the member's *current* role is always selectable, even if it has
  // since been made non-assignable (so editing the rest of the form never
  // silently drops it). Otherwise the grid is exactly the assignable list.
  const mergedRoleOptions = useMemo<RoleOption[]>(() => {
    if (member.role && !roleOptions.some((r) => r.key === member.role)) {
      return [...roleOptions, { key: member.role, name: member.role }];
    }
    return roleOptions;
  }, [roleOptions, member.role]);

  // Esc closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while the drawer is open (matches the Popup component).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const scopeLocked = !!SCOPE_LOCKED_ROLES[role];

  const editableRows = useMemo(
    () =>
      accessibleShops
        .slice()
        .sort((a, b) =>
          (a.brand_name || a.name || a.slug).localeCompare(
            b.brand_name || b.name || b.slug
          )
        ),
    [accessibleShops]
  );

  // Grants the member holds outside the current user's visibility — shown
  // read-only so they're never silently dropped on save.
  const lockedRows = useMemo(
    () => (member.assigned_shops ?? []).filter((s) => !editableSlugs[s.slug]),
    [member.assigned_shops, editableSlugs]
  );

  const grantedCount = Object.keys(brandPerms).length + lockedRows.length;
  const allOn =
    editableRows.length > 0 && editableRows.every((s) => !!brandPerms[s.slug]);

  const toggleAll = () => {
    setBrandPerms((prev) => {
      if (allOn) return {};
      const next: Record<string, ShopPermission> = {};
      editableRows.forEach((s) => {
        next[s.slug] = prev[s.slug] || 'approve';
      });
      return next;
    });
  };

  const toggleBrand = (slug: string) => {
    setBrandPerms((prev) => {
      const next = { ...prev };
      if (next[slug]) delete next[slug];
      else next[slug] = 'approve';
      return next;
    });
  };

  const setPerm = (slug: string, perm: ShopPermission) =>
    setBrandPerms((prev) => ({ ...prev, [slug]: perm }));

  const mcpChanged = useMemo(
    () =>
      !sameMcpAccess(
        { mode: mcpMode, platforms: mcpPlatforms },
        initialMcp
      ),
    [mcpMode, mcpPlatforms, initialMcp]
  );

  const dirty = useMemo(() => {
    const profileChanged =
      name.trim() !== (member.name || '') ||
      role !== member.role ||
      isLead !== !!member.is_team_lead ||
      active !== (member.active !== false);
    if (profileChanged) return true;
    if (mcpChanged) return true;
    if (scopeLocked) return false;
    return serializePerms(brandPerms) !== serializePerms(initialBrandPerms);
  }, [
    name,
    role,
    isLead,
    active,
    mcpChanged,
    scopeLocked,
    brandPerms,
    initialBrandPerms,
    member,
  ]);

  const handleSave = () => {
    if (!dirty || saving) return;
    onSave(member, {
      name: name.trim() || member.email,
      role,
      is_team_lead: isLead,
      active,
      brandPerms: scopeLocked ? null : brandPerms,
      mcpAccess: mcpChanged ? mcpAccessWire(mcpMode, mcpPlatforms) : null,
    });
  };

  // The selected role's own allowlist, for the effective-set hint.
  const selectedRoleMcp = useMemo(
    () => roleOptions.find((r) => r.key === role)?.mcpAccess,
    [roleOptions, role]
  );

  return createPortal(
    <>
      <div className='teamDrawer__scrim' onClick={onClose} />
      <aside
        className='teamDrawer teamDrawer--wide'
        role='dialog'
        aria-modal='true'
        aria-label={t('ManageAccess')}
      >
        <div className='teamDrawer__head'>
          <div className='teamDrawer__headLeft'>
            <Avatar name={member.name} email={member.email} />
            <div style={{ minWidth: 0 }}>
              <h3 className='teamDrawer__title'>
                {member.name || member.email}
              </h3>
              <p className='teamDrawer__sub'>{member.email}</p>
            </div>
          </div>
          <button
            type='button'
            className='teamDrawer__close'
            onClick={onClose}
            aria-label={t('Cancel')}
          >
            <Icon name='x' size={18} />
          </button>
        </div>

        <div className='teamDrawer__body'>
          {/* Name */}
          <div className='dsec'>
            <label className='fieldLabel' htmlFor='access-name'>
              {t('FullNameLabel')}
            </label>
            <input
              id='access-name'
              className='textInput'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('NamePlaceholder')}
            />
          </div>

          {/* Role */}
          <div className='dsec'>
            <div className='dsec__label'>
              {t('Role')}
              <span className='dsec__hint'>{t('RoleSectionHint')}</span>
            </div>
            <RoleGrid
              value={role}
              roles={mergedRoleOptions}
              onChange={setRole}
            />
            {onlyOwner && (
              <p className='scopeFoot' data-testid='team-only-owner-note'>
                {t('TeamOnlyOwnerLocked')}
              </p>
            )}
            <div style={{ marginTop: '1.1rem' }}>
              <div className='toggleRow'>
                <span className='toggleRow__label'>
                  <strong>{t('TeamLead')}</strong>
                  <span>{t('TeamLeadHint')}</span>
                </span>
                <Toggle on={isLead} onChange={setIsLead} />
              </div>
              <div className='toggleRow'>
                <span className='toggleRow__label'>
                  <strong>{t('Active')}</strong>
                  <span>{t('MemberActiveHint')}</span>
                </span>
                <Toggle on={active} onChange={setActive} disabled={onlyOwner} />
              </div>
            </div>
          </div>

          {/* Brand access */}
          <div className='dsec'>
            <div className='dsec__label'>
              {t('BrandAccess')}
              <span className='dsec__hint'>{t('ScopeSectionHint')}</span>
            </div>
            {scopeLocked ? (
              <OwnerFullPanel />
            ) : (
              <>
                {editableRows.length > 0 && (
                  <div className='scopeHead'>
                    <span className='dsec__hint'>
                      {grantedCount === 0
                        ? t('TeamNoBrandsShort')
                        : grantedCount === 1
                        ? t('TeamOneBrand')
                        : t('TeamBrandsN', { count: grantedCount })}
                    </span>
                    <button
                      type='button'
                      className='selectAll'
                      onClick={toggleAll}
                    >
                      {allOn ? t('TeamClearAll') : t('TeamSelectAll')}
                    </button>
                  </div>
                )}

                {editableRows.length === 0 && lockedRows.length === 0 ? (
                  <div className='scopeFoot'>{t('TeamNoBrandsToGrant')}</div>
                ) : (
                  <div className='scopeList'>
                    {editableRows.map((s) => {
                      const on = !!brandPerms[s.slug];
                      return (
                        <div
                          key={s.slug}
                          className={`scopeRow ${on ? 'scopeRow--on' : ''}`}
                        >
                          <span className='scopeRow__left'>
                            <span className='scopeRow__name'>
                              {s.brand_name || s.name || s.slug}
                            </span>
                            <span className='scopeRow__sub'>{s.slug}</span>
                          </span>
                          <span className='scopeRow__right'>
                            {on && (
                              <select
                                className='permSelect'
                                value={brandPerms[s.slug]}
                                onChange={(e) =>
                                  setPerm(
                                    s.slug,
                                    e.target.value as ShopPermission
                                  )
                                }
                              >
                                <option value='approve'>
                                  {t('PermissionLevelManager')}
                                </option>
                                <option value='propose'>
                                  {t('PermissionLevelAnalyst')}
                                </option>
                                <option value='read'>
                                  {t('PermissionLevelReadOnly')}
                                </option>
                              </select>
                            )}
                            <Toggle
                              on={on}
                              onChange={() => toggleBrand(s.slug)}
                            />
                          </span>
                        </div>
                      );
                    })}

                    {lockedRows.map((s) => (
                      <div key={s.slug} className='scopeRow scopeRow--locked'>
                        <span className='scopeRow__left'>
                          <span className='scopeRow__name'>
                            {s.brand_name || s.name || s.slug}
                          </span>
                          <span className='scopeRow__sub'>
                            <Icon name='lock' size={12} />
                            {t('TeamBrandLockedHint')}
                          </span>
                        </span>
                        <span className='scopeRow__right'>
                          <span className='dsec__hint'>
                            {normPerm(s.permission_level) === 'propose'
                              ? t('PermissionLevelAnalyst')
                              : normPerm(s.permission_level) === 'read'
                              ? t('PermissionLevelReadOnly')
                              : t('PermissionLevelManager')}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <p className='scopeFoot'>{t('TeamScopeFoot')}</p>
                <div className='scopeNote'>
                  <Icon name='info' size={15} />
                  <span>{t('TeamToolsMcpNote')}</span>
                </div>
              </>
            )}
          </div>

          {/* Platform (MCP) access — brand-independent per-member allowlist */}
          <PlatformAccessSection
            allPlatforms={allPlatforms}
            mode={mcpMode}
            platforms={mcpPlatforms}
            roleMcpAccess={selectedRoleMcp}
            onModeChange={setMcpMode}
            onTogglePlatform={toggleMcpPlatform}
          />
        </div>

        <div className='teamDrawer__foot'>
          <div className='teamDrawer__footRow'>
            <button
              type='button'
              className='drawerBtn drawerBtn--ghost'
              onClick={onClose}
              disabled={saving}
            >
              {t('Cancel')}
            </button>
            <button
              type='button'
              className='drawerBtn drawerBtn--primary'
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? t('Saving') : t('SaveChanges')}
            </button>
          </div>
        </div>
      </aside>
    </>,
    document.body
  );
};

// ============================================================================
//  Invite drawer (new teammate: email + role + brand scope)
// ============================================================================

// The per-brand access level picker of the invite and add-with-key drawers.
const BrandLevelSelect: React.FC<{
  slug: string;
  value: ShopPermission;
  onChange: (level: ShopPermission) => void;
}> = ({ slug, value, onChange }) => {
  const { t } = useTranslation();
  return (
    <select
      className='permSelect'
      data-testid={`brand-level-${slug}`}
      aria-label={t('BrandAccessLevel')}
      value={value}
      onChange={(e) => onChange(e.target.value as ShopPermission)}
    >
      <option value='approve'>{t('PermissionLevelManager')}</option>
      <option value='propose'>{t('PermissionLevelAnalyst')}</option>
      <option value='read'>{t('PermissionLevelReadOnly')}</option>
    </select>
  );
};

interface InviteSavePayload {
  email: string;
  name: string;
  role: string;
  // Per-brand permission map (read/propose/approve); empty = no brands granted.
  brandPerms: Record<string, ShopPermission>;
  // Per-member platform allowlist set at invite time, or null to inherit role.
  mcpAccess: McpAccess | null;
}

interface InviteDrawerProps {
  accessibleShops: AgencyAccessibleShop[];
  roleOptions: RoleOption[];
  allPlatforms: PlatformDef[];
  submitting: boolean;
  // Set when the API refused the invite because the address already has a
  // pending invitation; the drawer then offers resend or replace.
  pending: PendingInvitation | null;
  // Set when the API refused the invite for another reason.
  refusal: DrawerRefusal | null;
  onEmailChange: () => void;
  onClose: () => void;
  onInvite: (
    payload: InviteSavePayload,
    options?: { replacePending?: boolean }
  ) => void;
  onResendPending: (employeeId: string) => void;
}

const InviteDrawer: React.FC<InviteDrawerProps> = ({
  accessibleShops,
  roleOptions,
  allPlatforms,
  submitting,
  pending,
  refusal,
  onEmailChange,
  onClose,
  onInvite,
  onResendPending,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // A field says why it is refused once it has been left.
  const [emailTouched, setEmailTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  // Default to "analyst" when offered, else the first assignable role.
  const [role, setRole] = useState<string>(
    () =>
      (roleOptions.some((r) => r.key === 'analyst')
        ? 'analyst'
        : roleOptions[0]?.key) ?? 'analyst'
  );
  // Per-brand permission map, exactly like the add-with-key drawer.
  const [brandPerms, setBrandPerms] = useState<Record<string, ShopPermission>>(
    {}
  );
  // Brands whose level the user changed by hand; every other picked brand
  // follows the default of the chosen role.
  const [levelSetByHand, setLevelSetByHand] = useState<Record<string, boolean>>(
    {}
  );
  const roleLevel = defaultBrandLevel(role, roleOptions);
  const changeRole = (next: string) => {
    setRole(next);
    setBrandPerms((prev) =>
      followRoleDefault(
        prev,
        levelSetByHand,
        defaultBrandLevel(next, roleOptions)
      )
    );
  };

  // Optional per-member platform allowlist for the new member.
  const [mcpMode, setMcpMode] = useState<McpAccessMode>('all');
  const [mcpPlatforms, setMcpPlatforms] = useState<Set<string>>(new Set());
  const toggleMcpPlatform = (key: string) =>
    setMcpPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while the drawer is open (matches the Popup component).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const scopeLocked = !!SCOPE_LOCKED_ROLES[role];

  const sortedShops = useMemo(
    () =>
      accessibleShops
        .slice()
        .sort((a, b) =>
          (a.brand_name || a.name || a.slug).localeCompare(
            b.brand_name || b.name || b.slug
          )
        ),
    [accessibleShops]
  );

  const count = Object.keys(brandPerms).length;
  const allOn =
    sortedShops.length > 0 && sortedShops.every((s) => !!brandPerms[s.slug]);
  const valid = memberFormValid(email, name);
  const fieldErrors = memberFormErrors(
    { email, name },
    { email: emailTouched, name: nameTouched }
  );

  const toggleAll = () => {
    if (allOn) setLevelSetByHand({});
    setBrandPerms((prev) => {
      if (allOn) return {};
      const next: Record<string, ShopPermission> = {};
      sortedShops.forEach((s) => {
        next[s.slug] = prev[s.slug] || roleLevel;
      });
      return next;
    });
  };
  const toggleBrand = (slug: string) => {
    setLevelSetByHand((prev) => ({ ...prev, [slug]: false }));
    setBrandPerms((prev) => {
      const next = { ...prev };
      if (next[slug]) delete next[slug];
      else next[slug] = roleLevel;
      return next;
    });
  };
  const setPerm = (slug: string, perm: ShopPermission) => {
    setLevelSetByHand((prev) => ({ ...prev, [slug]: true }));
    setBrandPerms((prev) => ({ ...prev, [slug]: perm }));
  };

  const summary = scopeLocked
    ? t('InviteSummaryFull')
    : count === 0
    ? t('InviteSummaryNone')
    : count === 1
    ? t('InviteSummaryOneBrand')
    : t('InviteSummaryBrands', { count });

  const selectedRoleMcp = useMemo(
    () => roleOptions.find((r) => r.key === role)?.mcpAccess,
    [roleOptions, role]
  );

  const handleInvite = (options?: { replacePending?: boolean }) => {
    if (!valid || submitting) return;
    onInvite(
      {
        email: email.trim(),
        name: name.trim(),
        role,
        brandPerms: scopeLocked ? {} : brandPerms,
        mcpAccess:
          mcpMode === 'allowlist'
            ? mcpAccessWire(mcpMode, mcpPlatforms)
            : null,
      },
      options
    );
  };

  // The pending-invitation notice belongs to the address it was reported for:
  // it goes away as soon as the email in the form is another one.
  const pendingForThisEmail =
    pending &&
    (!pending.employee.email ||
      pending.employee.email.trim().toLowerCase() ===
        email.trim().toLowerCase())
      ? pending
      : null;

  return createPortal(
    <>
      <div className='teamDrawer__scrim' onClick={onClose} />
      <aside
        className='teamDrawer teamDrawer--wide'
        role='dialog'
        aria-modal='true'
        aria-label={t('InviteEmployee')}
      >
        <div className='teamDrawer__head'>
          <div className='teamDrawer__headLeft'>
            <span className='teamDrawer__inviteIcon'>
              <Icon name='send' size={18} />
            </span>
            <div style={{ minWidth: 0 }}>
              <h3 className='teamDrawer__title'>{t('InviteEmployee')}</h3>
              <p className='teamDrawer__sub'>{t('InviteDrawerSubtitle')}</p>
            </div>
          </div>
          <button
            type='button'
            className='teamDrawer__close'
            onClick={onClose}
            aria-label={t('Cancel')}
          >
            <Icon name='x' size={18} />
          </button>
        </div>

        <div className='teamDrawer__body'>
          <div className='dsec'>
            <label className='fieldLabel' htmlFor='invite-email'>
              {t('WorkEmail')}
            </label>
            <input
              id='invite-email'
              className='textInput'
              type='email'
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                onEmailChange();
              }}
              onBlur={() => setEmailTouched(true)}
              aria-invalid={!!fieldErrors.email || !!refusal?.email}
              placeholder={t('EmailPlaceholder')}
            />
            {fieldErrors.email && (
              <p className='teamFieldError' role='alert'>
                {t(fieldErrors.email)}
              </p>
            )}
            {!fieldErrors.email && refusal?.email && (
              <p
                className='teamFieldError'
                role='alert'
                data-testid='invite-email-refusal'
              >
                {refusal.email}
              </p>
            )}
            <div style={{ height: '1.2rem' }} />
            <label className='fieldLabel' htmlFor='invite-name'>
              {t('FullNameLabel')}
            </label>
            <input
              id='invite-name'
              className='textInput'
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              aria-invalid={!!fieldErrors.name}
              placeholder={t('NamePlaceholder')}
            />
            {fieldErrors.name && (
              <p className='teamFieldError' role='alert'>
                {t(fieldErrors.name)}
              </p>
            )}
          </div>

          <div className='dsec'>
            <div className='dsec__label'>
              {t('Role')}
              <span className='dsec__hint'>{t('RoleSectionHint')}</span>
            </div>
            <RoleGrid value={role} roles={roleOptions} onChange={changeRole} />
          </div>

          <div className='dsec'>
            <div className='dsec__label'>
              {t('BrandAccess')}
              <span className='dsec__hint'>{t('InviteBrandScopeHint')}</span>
            </div>
            {scopeLocked ? (
              <OwnerFullPanel />
            ) : sortedShops.length === 0 ? (
              <div className='scopeFoot'>{t('TeamNoBrandsToGrant')}</div>
            ) : (
              <>
                <div className='scopeHead'>
                  <span className='dsec__hint'>{summary}</span>
                  <button
                    type='button'
                    className='selectAll'
                    onClick={toggleAll}
                  >
                    {allOn ? t('TeamClearAll') : t('TeamSelectAll')}
                  </button>
                </div>
                <div className='scopeList'>
                  {sortedShops.map((s) => {
                    const on = !!brandPerms[s.slug];
                    return (
                      <div
                        key={s.slug}
                        className={`scopeRow ${on ? 'scopeRow--on' : ''}`}
                      >
                        <span className='scopeRow__left'>
                          <span className='scopeRow__name'>
                            {s.brand_name || s.name || s.slug}
                          </span>
                          <span className='scopeRow__sub'>{s.slug}</span>
                        </span>
                        <span className='scopeRow__right'>
                          {on && (
                            <BrandLevelSelect
                              slug={s.slug}
                              value={brandPerms[s.slug]}
                              onChange={(level) => setPerm(s.slug, level)}
                            />
                          )}
                          <Toggle on={on} onChange={() => toggleBrand(s.slug)} />
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className='scopeNote'>
                  <Icon name='info' size={15} />
                  <span>{t('TeamToolsMcpNote')}</span>
                </div>
              </>
            )}
          </div>

          {/* Platform (MCP) access — optional per-member allowlist at invite */}
          <PlatformAccessSection
            allPlatforms={allPlatforms}
            mode={mcpMode}
            platforms={mcpPlatforms}
            roleMcpAccess={selectedRoleMcp}
            onModeChange={setMcpMode}
            onTogglePlatform={toggleMcpPlatform}
          />
        </div>

        <div className='teamDrawer__foot'>
          {refusal?.form && (
            <p
              className='teamFieldError'
              role='alert'
              data-testid='invite-refusal'
            >
              {refusal.form}
            </p>
          )}
          {pendingForThisEmail && (
            <PendingInvitationNotice
              reason={pendingForThisEmail.reason}
              roleLabel={
                roleOptions.find((r) => r.key === role)?.name ?? role
              }
              busy={submitting}
              onResend={() => onResendPending(pendingForThisEmail.employee.id)}
              onReplace={() => handleInvite({ replacePending: true })}
            />
          )}
          <div className='teamDrawer__footSummary'>{summary}</div>
          <div className='teamDrawer__footRow'>
            <button
              type='button'
              className='drawerBtn drawerBtn--ghost'
              onClick={onClose}
              disabled={submitting}
            >
              {t('Cancel')}
            </button>
            <button
              type='button'
              className='drawerBtn drawerBtn--primary'
              onClick={() => handleInvite()}
              disabled={!valid || submitting || !!pendingForThisEmail}
            >
              {submitting ? t('Saving') : t('SendInvite')}
            </button>
          </div>
        </div>
      </aside>
    </>,
    document.body
  );
};

// ============================================================================
//  Add-with-key drawer (new teammate created immediately, key handed over now)
// ============================================================================
//
// Same identity fields as the Invite drawer, but with the Manage-access
// drawer's per-brand access-level picker (read/propose/approve) so this path is
// not less capable than invite. On save the page sends every picked brand
// with its level (shop_permissions) and returns the one-time `emp_` key.

interface AddKeySavePayload {
  email: string;
  name: string;
  role: string;
  // Per-brand permission map (read/propose/approve); empty = no brands granted.
  brandPerms: Record<string, ShopPermission>;
  // Per-member platform allowlist set at creation, or null to inherit the role.
  mcpAccess: McpAccess | null;
}

interface AddKeyDrawerProps {
  accessibleShops: AgencyAccessibleShop[];
  roleOptions: RoleOption[];
  allPlatforms: PlatformDef[];
  submitting: boolean;
  // Set when the API refused to add the member.
  refusal: DrawerRefusal | null;
  onEmailChange: () => void;
  onClose: () => void;
  onCreate: (payload: AddKeySavePayload) => void;
}

const AddKeyDrawer: React.FC<AddKeyDrawerProps> = ({
  accessibleShops,
  roleOptions,
  allPlatforms,
  submitting,
  refusal,
  onEmailChange,
  onClose,
  onCreate,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // A field says why it is refused once it has been left.
  const [emailTouched, setEmailTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  // Default to "analyst" when offered, else the first assignable role.
  const [role, setRole] = useState<string>(
    () =>
      (roleOptions.some((r) => r.key === 'analyst')
        ? 'analyst'
        : roleOptions[0]?.key) ?? 'analyst'
  );
  // Per-brand permission map, exactly like the Manage-access drawer.
  const [brandPerms, setBrandPerms] = useState<Record<string, ShopPermission>>(
    {}
  );
  // Brands whose level the user changed by hand; every other picked brand
  // follows the default of the chosen role.
  const [levelSetByHand, setLevelSetByHand] = useState<Record<string, boolean>>(
    {}
  );
  const roleLevel = defaultBrandLevel(role, roleOptions);
  const changeRole = (next: string) => {
    setRole(next);
    setBrandPerms((prev) =>
      followRoleDefault(
        prev,
        levelSetByHand,
        defaultBrandLevel(next, roleOptions)
      )
    );
  };

  // Optional per-member platform allowlist for the new member.
  const [mcpMode, setMcpMode] = useState<McpAccessMode>('all');
  const [mcpPlatforms, setMcpPlatforms] = useState<Set<string>>(new Set());
  const toggleMcpPlatform = (key: string) =>
    setMcpPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while the drawer is open (matches the Popup component).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const scopeLocked = !!SCOPE_LOCKED_ROLES[role];

  const sortedShops = useMemo(
    () =>
      accessibleShops
        .slice()
        .sort((a, b) =>
          (a.brand_name || a.name || a.slug).localeCompare(
            b.brand_name || b.name || b.slug
          )
        ),
    [accessibleShops]
  );

  const grantedCount = Object.keys(brandPerms).length;
  const allOn =
    sortedShops.length > 0 && sortedShops.every((s) => !!brandPerms[s.slug]);
  const valid = memberFormValid(email, name);
  const fieldErrors = memberFormErrors(
    { email, name },
    { email: emailTouched, name: nameTouched }
  );

  const toggleAll = () => {
    if (allOn) setLevelSetByHand({});
    setBrandPerms((prev) => {
      if (allOn) return {};
      const next: Record<string, ShopPermission> = {};
      sortedShops.forEach((s) => {
        next[s.slug] = prev[s.slug] || roleLevel;
      });
      return next;
    });
  };
  const toggleBrand = (slug: string) => {
    setLevelSetByHand((prev) => ({ ...prev, [slug]: false }));
    setBrandPerms((prev) => {
      const next = { ...prev };
      if (next[slug]) delete next[slug];
      else next[slug] = roleLevel;
      return next;
    });
  };
  const setPerm = (slug: string, perm: ShopPermission) => {
    setLevelSetByHand((prev) => ({ ...prev, [slug]: true }));
    setBrandPerms((prev) => ({ ...prev, [slug]: perm }));
  };

  const summary = scopeLocked
    ? t('InviteSummaryFull')
    : grantedCount === 0
    ? t('InviteSummaryNone')
    : grantedCount === 1
    ? t('InviteSummaryOneBrand')
    : t('InviteSummaryBrands', { count: grantedCount });

  const selectedRoleMcp = useMemo(
    () => roleOptions.find((r) => r.key === role)?.mcpAccess,
    [roleOptions, role]
  );

  const handleCreate = () => {
    if (!valid || submitting) return;
    onCreate({
      email: email.trim(),
      name: name.trim(),
      role,
      brandPerms: scopeLocked ? {} : brandPerms,
      mcpAccess:
        mcpMode === 'allowlist'
          ? mcpAccessWire(mcpMode, mcpPlatforms)
          : null,
    });
  };

  return createPortal(
    <>
      <div className='teamDrawer__scrim' onClick={onClose} />
      <aside
        className='teamDrawer teamDrawer--wide'
        role='dialog'
        aria-modal='true'
        aria-label={t('TeamAddWithKey', 'Add employee & generate key now')}
      >
        <div className='teamDrawer__head'>
          <div className='teamDrawer__headLeft'>
            <span className='teamDrawer__inviteIcon'>
              <Icon name='lock' size={18} />
            </span>
            <div style={{ minWidth: 0 }}>
              <h3 className='teamDrawer__title'>
                {t('TeamAddWithKey', 'Add employee & generate key now')}
              </h3>
              <p className='teamDrawer__sub'>
                {t(
                  'TeamAddWithKeySubtitle',
                  'Creates the member and generates their API key immediately. The key is shown once — you hand it over yourself.'
                )}
              </p>
            </div>
          </div>
          <button
            type='button'
            className='teamDrawer__close'
            onClick={onClose}
            aria-label={t('Cancel')}
          >
            <Icon name='x' size={18} />
          </button>
        </div>

        <div className='teamDrawer__body'>
          <div className='dsec'>
            <label className='fieldLabel' htmlFor='addkey-email'>
              {t('WorkEmail')}
            </label>
            <input
              id='addkey-email'
              className='textInput'
              type='email'
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                onEmailChange();
              }}
              onBlur={() => setEmailTouched(true)}
              aria-invalid={!!fieldErrors.email || !!refusal?.email}
              placeholder={t('EmailPlaceholder')}
            />
            {fieldErrors.email && (
              <p className='teamFieldError' role='alert'>
                {t(fieldErrors.email)}
              </p>
            )}
            {!fieldErrors.email && refusal?.email && (
              <p
                className='teamFieldError'
                role='alert'
                data-testid='addkey-email-refusal'
              >
                {refusal.email}
              </p>
            )}
            <div style={{ height: '1.2rem' }} />
            <label className='fieldLabel' htmlFor='addkey-name'>
              {t('FullNameLabel')}
            </label>
            <input
              id='addkey-name'
              className='textInput'
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              aria-invalid={!!fieldErrors.name}
              placeholder={t('NamePlaceholder')}
            />
            {fieldErrors.name && (
              <p className='teamFieldError' role='alert'>
                {t(fieldErrors.name)}
              </p>
            )}
          </div>

          <div className='dsec'>
            <div className='dsec__label'>
              {t('Role')}
              <span className='dsec__hint'>{t('RoleSectionHint')}</span>
            </div>
            <RoleGrid value={role} roles={roleOptions} onChange={changeRole} />
          </div>

          <div className='dsec'>
            <div className='dsec__label'>
              {t('BrandAccess')}
              <span className='dsec__hint'>{t('InviteBrandScopeHint')}</span>
            </div>
            {scopeLocked ? (
              <OwnerFullPanel />
            ) : sortedShops.length === 0 ? (
              <div className='scopeFoot'>{t('TeamNoBrandsToGrant')}</div>
            ) : (
              <>
                <div className='scopeHead'>
                  <span className='dsec__hint'>{summary}</span>
                  <button
                    type='button'
                    className='selectAll'
                    onClick={toggleAll}
                  >
                    {allOn ? t('TeamClearAll') : t('TeamSelectAll')}
                  </button>
                </div>
                <div className='scopeList'>
                  {sortedShops.map((s) => {
                    const on = !!brandPerms[s.slug];
                    return (
                      <div
                        key={s.slug}
                        className={`scopeRow ${on ? 'scopeRow--on' : ''}`}
                      >
                        <span className='scopeRow__left'>
                          <span className='scopeRow__name'>
                            {s.brand_name || s.name || s.slug}
                          </span>
                          <span className='scopeRow__sub'>{s.slug}</span>
                        </span>
                        <span className='scopeRow__right'>
                          {on && (
                            <BrandLevelSelect
                              slug={s.slug}
                              value={brandPerms[s.slug]}
                              onChange={(level) => setPerm(s.slug, level)}
                            />
                          )}
                          <Toggle on={on} onChange={() => toggleBrand(s.slug)} />
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className='scopeNote'>
                  <Icon name='info' size={15} />
                  <span>{t('TeamToolsMcpNote')}</span>
                </div>
              </>
            )}
          </div>

          {/* Platform (MCP) access — optional per-member allowlist */}
          <PlatformAccessSection
            allPlatforms={allPlatforms}
            mode={mcpMode}
            platforms={mcpPlatforms}
            roleMcpAccess={selectedRoleMcp}
            onModeChange={setMcpMode}
            onTogglePlatform={toggleMcpPlatform}
          />
        </div>

        <div className='teamDrawer__foot'>
          {refusal?.form && (
            <p
              className='teamFieldError'
              role='alert'
              data-testid='addkey-refusal'
            >
              {refusal.form}
            </p>
          )}
          <div className='teamDrawer__footSummary'>{summary}</div>
          <div className='teamDrawer__footRow'>
            <button
              type='button'
              className='drawerBtn drawerBtn--ghost'
              onClick={onClose}
              disabled={submitting}
            >
              {t('Cancel')}
            </button>
            <button
              type='button'
              className='drawerBtn drawerBtn--primary'
              onClick={handleCreate}
              disabled={!valid || submitting}
            >
              {submitting
                ? t('Saving')
                : t('TeamAddWithKeySubmit', 'Add & generate key')}
            </button>
          </div>
        </div>
      </aside>
    </>,
    document.body
  );
};

// ============================================================================
//  Page
// ============================================================================

const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const role = useEmployeeRole();
  const identity = useSelector(
    (s: RootState) => s.auth.agency.employeeIdentity
  );
  const accessibleShops = useMemo(
    () => identity?.accessible_shops ?? [],
    [identity?.accessible_shops]
  );

  // Resolved like the menu entry and the RoleGates below: the member's live
  // capability list when the profile carries one, the role matrix otherwise.
  const liveCapabilities = useCapabilities();
  const canViewTeam = canViewTeamPage(liveCapabilities, role);
  const canInvite = canResolve(liveCapabilities, role, 'team.invite');
  // Relocated from Reporting: invite/manage whitelabel-portal clients. Gated on
  // the EXISTING `portal.clients.manage` capability (owner/manager/team_lead).
  const canManageClients = canResolve(
    liveCapabilities,
    role,
    'portal.clients.manage'
  );

  // ----------------------- data -----------------------
  const [employees, setEmployees] = useState<AgencyEmployee[]>([]);
  const [groups, setGroups] = useState<AgencyGroup[]>([]);
  // MCP connector catalog for the drawers' "Platform access" editor.
  const [allPlatforms, setAllPlatforms] = useState<PlatformDef[]>([]);
  // Org roles from GET /agency/roles — drive every role selector on this page.
  const [orgRoles, setOrgRoles] = useState<AgencyRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  // In-page section tabs: Employees / Teams / Clients / Team activity.
  const [activeTab, setActiveTab] = useState<
    'employees' | 'teams' | 'clients' | 'activity'
  >('employees');
  // Team activity is owner only. The role falls back to owner before /me
  // resolves, so a 403 from the activity endpoint also hides the tab.
  const [activityForbidden, setActivityForbidden] = useState(false);
  const canViewActivity = showTeamActivityTab(role, activityForbidden);
  const handleActivityForbidden = useCallback(() => {
    setActivityForbidden(true);
    setActiveTab((tab) => (tab === 'activity' ? 'employees' : tab));
  }, []);

  // ----------------------- invite drawer -----------------------
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [invitePending, setInvitePending] = useState<PendingInvitation | null>(
    null
  );
  const [invitationResult, setInvitationResult] =
    useState<EmployeeInvitation | null>(null);

  // ----------------------- add-with-key drawer -----------------------
  const [addKeyOpen, setAddKeyOpen] = useState(false);
  const [addKeySubmitting, setAddKeySubmitting] = useState(false);
  // Why the open invite / add-with-key drawer's last request was refused.
  const [inviteRefusal, setInviteRefusal] = useState<DrawerRefusal | null>(
    null
  );
  const [addKeyRefusal, setAddKeyRefusal] = useState<DrawerRefusal | null>(
    null
  );
  // The one-time key result — drives the "shown once" key popup.
  const [keyResult, setKeyResult] = useState<CreateEmployeeResponse | null>(
    null
  );

  // ----------------------- manage-access drawer -----------------------
  const [accessTargetId, setAccessTargetId] = useState<string | null>(null);
  const [accessSaving, setAccessSaving] = useState(false);

  // ----------------------- resend / delete confirms -----------------------
  const [resendTarget, setResendTarget] = useState<AgencyEmployee | null>(null);
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgencyEmployee | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // ----------------------- group create / manage -----------------------
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupLead, setGroupLead] = useState('');
  const [groupSubmitting, setGroupSubmitting] = useState(false);
  // A refused team request is shown inside its dialog: the page banner sits
  // behind the dialog's overlay.
  const [groupError, setGroupError] = useState<string | null>(null);
  const [groupManageError, setGroupManageError] = useState<string | null>(
    null
  );
  const [groupManageId, setGroupManageId] = useState<string | null>(null);
  // Delete-team confirmation, shown in place of the manage dialog's content.
  const [groupDeleteConfirm, setGroupDeleteConfirm] = useState(false);
  const [groupDeleteSubmitting, setGroupDeleteSubmitting] = useState(false);
  const [groupDeleteError, setGroupDeleteError] = useState<string | null>(
    null
  );
  const [addMemberEmployeeId, setAddMemberEmployeeId] = useState('');
  const [addMemberSubmitting, setAddMemberSubmitting] = useState(false);
  // Phase 13 — role a group's members inherit ('' = none).
  const [groupRole, setGroupRole] = useState('');

  // ----------------------- helpers -----------------------
  const showError = (msg: string) => {
    setError(msg);
    dispatch(setMessage({ messageType: 'error', message: msg }));
  };
  const showSuccess = (msg: string) => {
    dispatch(setMessage({ messageType: 'success', message: msg }));
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Roles are best-effort: a failure there must not blank the whole page
      // (the selectors fall back to the static system roles), so it's settled
      // independently of the employees/groups load.
      const [empsRes, grps, rolesRes] = await Promise.all([
        listEmployeesWithPlatforms(),
        listGroups(),
        listRoles().catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[TeamPage] roles load failed; using fallback', err);
          return null;
        }),
      ]);
      setEmployees(empsRes.employees);
      // Prefer the roles endpoint's catalog (identical set); fall back to the
      // one the employees index returns so the editor is never empty.
      setAllPlatforms(
        rolesRes?.all_platforms?.length
          ? rolesRes.all_platforms
          : empsRes.all_platforms
      );
      setGroups(grps);
      if (rolesRes) setOrgRoles(rolesRes.data);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[TeamPage] load failed', err);
      showError(err?.message || 'Could not load your team.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewTeam) refresh();
    else setLoading(false);
  }, [canViewTeam, refresh]);

  // ----------------------- derived -----------------------
  const accessTarget = useMemo(
    () => employees.find((e) => e.id === accessTargetId) || null,
    [employees, accessTargetId]
  );
  const managedGroup = useMemo(
    () => groups.find((g) => g.id === groupManageId) || null,
    [groups, groupManageId]
  );

  // empId → groups it belongs to (groups carry no scope bundle in the backend).
  const memberGroups = useMemo(() => {
    const map: Record<string, AgencyGroup[]> = {};
    groups.forEach((g) => {
      (g.members ?? []).forEach((m) => {
        if (!map[m.id]) map[m.id] = [];
        map[m.id].push(g);
      });
    });
    return map;
  }, [groups]);

  // Roles offered in every selector: system + assignable custom roles, in the
  // order the API returns. Falls back to the static system keys when the roles
  // endpoint hasn't resolved (or failed), so the selectors are never empty.
  const roleOptions = useMemo<RoleOption[]>(() => {
    const assignable = orgRoles.filter((r) => r.is_system || r.is_assignable);
    if (assignable.length > 0) {
      return assignable.map((r) => ({
        key: r.key,
        name: r.name,
        description: r.description,
        mcpAccess: r.mcp_access,
        capabilities: r.capabilities,
      }));
    }
    return ROLE_OPTIONS.map((k) => ({ key: k, name: t('Role_' + k) }));
  }, [orgRoles, t]);

  // The roles this member may give: an owner all of them, anyone else only
  // the roles below their own (the API refuses the rest).
  const givableRoleOptions = useMemo<RoleOption[]>(
    () => assignableRoles(role, roleOptions, orgRoles),
    [role, roleOptions, orgRoles]
  );
  const rankCaller = useMemo(
    () => ({ role, id: identity?.employee_id }),
    [role, identity?.employee_id]
  );
  const accessTargetOnlyOwner =
    !!accessTarget && isOnlyOwner(accessTarget, employees);
  // Role choices in the member drawer: the roles this member may give, plus
  // the row's current role so saving other fields never drops it. The only
  // owner keeps the owner role.
  const accessRoleOptions = useMemo<RoleOption[]>(() => {
    if (!accessTarget) return givableRoleOptions;
    const current = roleOptions.filter((r) => r.key === accessTarget.role);
    if (accessTargetOnlyOwner) return current;
    return givableRoleOptions.some((r) => r.key === accessTarget.role)
      ? givableRoleOptions
      : [...givableRoleOptions, ...current];
  }, [accessTarget, accessTargetOnlyOwner, givableRoleOptions, roleOptions]);

  // key → human name, for rendering a role badge wherever we only hold the key.
  const roleNameByKey = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    orgRoles.forEach((r) => {
      map[r.key] = r.name;
    });
    return map;
  }, [orgRoles]);

  // Resolve a role key to a display label: localized name for system roles,
  // the org's name for custom roles, else the raw key.
  const roleLabel = useCallback(
    (key?: string | null): string => {
      if (!key) return '';
      if (ROLE_TONE[key]) return t('Role_' + key);
      return roleNameByKey[key] || key;
    },
    [roleNameByKey, t]
  );

  // Filter pills: "All" + one per role offered.
  const roleFilters = useMemo<Array<{ value: string; label: string }>>(
    () => [
      { value: '', label: t('TeamRoleAll') },
      ...roleOptions.map((r) => ({ value: r.key, label: roleLabel(r.key) })),
    ],
    [roleOptions, roleLabel, t]
  );

  // Members are the accepted, active rows; an outstanding invitation is
  // counted on its own tile.
  const {
    members: memberCount,
    approvers: approverCount,
    pending: pendingCount,
  } = useMemo(
    () => teamCounts(employees, (role) => !!APPROVER_ROLES[role]),
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return employees.filter((e) => {
      if (roleFilter && e.role !== roleFilter) return false;
      if (!q) return true;
      return (
        e.email?.toLowerCase().includes(q) ||
        e.name?.toLowerCase().includes(q) ||
        e.role?.toLowerCase().includes(q)
      );
    });
  }, [employees, searchValue, roleFilter]);

  // ----------------------- invite -----------------------
  const submitInviteDrawer = async (
    payload: InviteSavePayload,
    options?: { replacePending?: boolean }
  ) => {
    setInviteSubmitting(true);
    setInviteRefusal(null);
    try {
      // Every picked brand goes out with its level. A pending invitation of
      // the same address is replaced only when the user asked for that.
      const invitation = await inviteEmployee(
        inviteMemberRequest(payload, options)
      );
      setInviteOpen(false);
      setInvitePending(null);
      setInvitationResult(invitation);
      showSuccess(t('TeamInviteCreated'));
      await refresh();
    } catch (err: any) {
      // The address already has a pending invitation: nothing was changed.
      // The drawer shows the reason and lets the user resend or replace it.
      const pending = pendingInvitationFrom(err);
      if (pending) {
        setInvitePending(pending);
      } else {
        // Shown inside the drawer, which stays open with what was entered.
        const refusal = drawerRefusalFrom(
          err,
          t(
            'TeamEmployeeAlreadyExists',
            'That email already belongs to a member of your team.'
          ),
          'Failed to invite employee.'
        );
        setInviteRefusal(refusal);
        if (refusal.form) showError(refusal.form);
      }
    } finally {
      setInviteSubmitting(false);
    }
  };

  // Resend the pending invitation the invite drawer reported, unchanged.
  const resendPendingInvite = async (employeeId: string) => {
    setInviteSubmitting(true);
    try {
      const invitation = await resendEmployeeInvitation(employeeId);
      setInviteOpen(false);
      setInvitePending(null);
      setInvitationResult(invitation);
      showSuccess(
        invitation.email_sent
          ? t('TeamInviteResentEmail')
          : t('TeamInviteResentLink')
      );
      await refresh();
    } catch (err: any) {
      showError(teamErrorText(err, 'Failed to resend invitation.'));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const copyInviteUrl = async () => {
    if (!invitationResult?.invite_url) return;
    try {
      await navigator.clipboard.writeText(invitationResult.invite_url);
      showSuccess(t('TeamInviteLinkCopied'));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[TeamPage] clipboard write failed', err);
      showError(t('TeamCopyFailed'));
    }
  };

  // ----------------------- add employee & generate key now -----------------------
  const submitAddKeyDrawer = async (payload: AddKeySavePayload) => {
    setAddKeySubmitting(true);
    setAddKeyRefusal(null);
    try {
      // Every picked brand goes out with its level in `shop_permissions`: a
      // brand sent without one would get the role's default, so an explicit
      // read-only pick would not stay read-only.
      const result = await createEmployee(addMemberRequest(payload));
      setAddKeyOpen(false);
      setKeyResult(result);
      showSuccess(t('TeamMemberCreated', 'Employee created.'));
      await refresh();
    } catch (err: any) {
      // The server refuses an email that already belongs to the team (422
      // employee_already_exists, with a sentence in `errors.email`): that goes
      // under the email field. Any other refusal is shown at the drawer's foot.
      // The drawer stays open with what was entered.
      const refusal = drawerRefusalFrom(
        err,
        t(
          'TeamEmployeeAlreadyExists',
          'That email already belongs to a member of your team.'
        ),
        t('TeamMemberCreateFailed', 'Failed to add employee.')
      );
      setAddKeyRefusal(refusal);
      if (refusal.form) showError(refusal.form);
    } finally {
      setAddKeySubmitting(false);
    }
  };

  const copyGeneratedKey = async () => {
    if (!keyResult?.api_key) return;
    try {
      await navigator.clipboard.writeText(keyResult.api_key);
      showSuccess(t('TeamKeyCopied', 'API key copied.'));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[TeamPage] clipboard write failed', err);
      showError(t('TeamCopyFailed'));
    }
  };

  // ----------------------- manage access (role + brand diff) -----------------------
  const saveAccess = async (
    member: AgencyEmployee,
    payload: AccessSavePayload
  ) => {
    setAccessSaving(true);
    try {
      const profileChanged =
        payload.name !== (member.name || '') ||
        payload.role !== member.role ||
        payload.is_team_lead !== !!member.is_team_lead ||
        payload.active !== (member.active !== false);

      // payload.mcpAccess is null when unchanged; otherwise the new allowlist.
      const mcpChanged = payload.mcpAccess !== null;

      if (profileChanged || mcpChanged) {
        await updateEmployee(member.id, {
          name: payload.name,
          role: payload.role,
          is_team_lead: payload.is_team_lead,
          active: payload.active,
          ...(mcpChanged ? { mcp_access: payload.mcpAccess } : {}),
        });
      }

      // Brand diff — only touches slugs the current user can grant, so a
      // member's hidden grants are preserved.
      if (payload.brandPerms) {
        const editable: Record<string, boolean> = {};
        accessibleShops.forEach((s) => {
          editable[s.slug] = true;
        });
        const initial: Record<string, ShopPermission> = {};
        (member.assigned_shops ?? []).forEach((s) => {
          if (editable[s.slug]) initial[s.slug] = normPerm(s.permission_level);
        });
        const next = payload.brandPerms;
        const ops: Array<() => Promise<void>> = [];
        Object.keys(editable).forEach((slug) => {
          const cur = next[slug];
          const init = initial[slug];
          if (init && !cur) {
            ops.push(() => revokeShopFromEmployee(member.id, slug));
          } else if (!init && cur) {
            ops.push(() => assignShopToEmployee(member.id, slug, cur));
          } else if (init && cur && init !== cur) {
            ops.push(() => revokeShopFromEmployee(member.id, slug));
            ops.push(() => assignShopToEmployee(member.id, slug, cur));
          }
        });
        // Sequential to avoid backend write races on the same employee.
        for (let i = 0; i < ops.length; i++) {
          // eslint-disable-next-line no-await-in-loop
          await ops[i]();
        }
      }

      setAccessTargetId(null);
      showSuccess(t('TeamMemberUpdated'));
      await refresh();
    } catch (err: any) {
      showError(teamErrorText(err, 'Failed to update access.'));
    } finally {
      setAccessSaving(false);
    }
  };

  // ----------------------- resend -----------------------
  const submitResend = async () => {
    if (!resendTarget) return;
    setResendSubmitting(true);
    try {
      const invitation = await resendEmployeeInvitation(resendTarget.id);
      setResendTarget(null);
      setInvitationResult(invitation);
      showSuccess(
        invitation.email_sent
          ? t('TeamInviteResentEmail')
          : t('TeamInviteResentLink')
      );
      await refresh();
    } catch (err: any) {
      showError(teamErrorText(err, 'Failed to resend invitation.'));
    } finally {
      setResendSubmitting(false);
    }
  };

  // ----------------------- delete -----------------------
  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await deleteEmployee(deleteTarget.id);
      setDeleteTarget(null);
      showSuccess(t('TeamMemberDeleted'));
      await refresh();
    } catch (err: any) {
      showError(teamErrorText(err, 'Failed to delete employee.'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ----------------------- groups -----------------------
  const openGroupDialog = () => {
    setGroupError(null);
    setGroupDialogOpen(true);
  };

  const openManageGroup = (groupId: string) => {
    setGroupManageError(null);
    setGroupDeleteConfirm(false);
    setGroupDeleteError(null);
    setAddMemberEmployeeId('');
    setGroupManageId(groupId);
  };

  // The select inputs report their value on mount too, so only a real change
  // of a field drops the error line.
  const changeGroupField =
    (current: string, set: (value: string) => void) => (value: string) => {
      if (value !== current) setGroupError(null);
      set(value);
    };

  const submitCreateGroup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    // The API requires a lead; nothing is sent without one.
    if (!teamFormReady(groupName, groupLead) || groupSubmitting) return;
    setGroupSubmitting(true);
    setGroupError(null);
    try {
      await createGroup({
        name: groupName.trim(),
        team_lead_id: groupLead,
        role: groupRole || null,
      });
      setGroupDialogOpen(false);
      setGroupName('');
      setGroupLead('');
      setGroupRole('');
      showSuccess(t('TeamGroupCreated'));
      await refresh();
    } catch (err: any) {
      setGroupError(teamDialogErrorText(err, t, t('TeamGroupCreateFailed')));
    } finally {
      setGroupSubmitting(false);
    }
  };

  const submitAddMember = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!managedGroup || !addMemberEmployeeId) return;
    setAddMemberSubmitting(true);
    setGroupManageError(null);
    try {
      await addGroupMember(managedGroup.id, addMemberEmployeeId);
      setAddMemberEmployeeId('');
      showSuccess(t('TeamGroupMemberAdded'));
      await refresh();
    } catch (err: any) {
      setGroupManageError(
        teamDialogErrorText(err, t, t('TeamGroupMemberAddFailed'))
      );
    } finally {
      setAddMemberSubmitting(false);
    }
  };

  // Phase 13 — change/clear the role a group's members inherit. Auto-saves on
  // selection via PATCH /agency/groups/{id}.
  const handleUpdateGroupRole = async (group: AgencyGroup, nextRole: string) => {
    if ((group.role ?? '') === nextRole) return;
    setGroupManageError(null);
    try {
      await updateGroup(group.id, { role: nextRole || null });
      showSuccess(t('TeamGroupRoleUpdated'));
      await refresh();
    } catch (err: any) {
      setGroupManageError(
        teamDialogErrorText(err, t, t('SomethingWentWrong-trying-again-msg'))
      );
    }
  };

  const handleRemoveMember = async (group: AgencyGroup, empId: string) => {
    setGroupManageError(null);
    try {
      await removeGroupMember(group.id, empId);
      showSuccess(t('TeamGroupMemberRemoved'));
      await refresh();
    } catch (err: any) {
      setGroupManageError(
        teamDialogErrorText(err, t, t('TeamGroupMemberRemoveFailed'))
      );
    }
  };

  const submitDeleteGroup = async () => {
    if (!managedGroup || groupDeleteSubmitting) return;
    setGroupDeleteSubmitting(true);
    setGroupDeleteError(null);
    try {
      await deleteGroup(managedGroup.id);
      setGroupDeleteConfirm(false);
      setGroupManageId(null);
      showSuccess(t('TeamGroupDeleted'));
      await refresh();
    } catch (err: any) {
      // The team stays listed; the reason is shown in the confirmation.
      setGroupDeleteError(
        teamDialogErrorText(err, t, t('TeamGroupDeleteFailed'))
      );
    } finally {
      setGroupDeleteSubmitting(false);
    }
  };

  // ----------------------- render: permission-denied -----------------------
  if (!canViewTeam) {
    return (
      <PageWrapperMain>
        <div className='subPage'>
          <SubPageContentHolder>
            <SubPageInnerContentHolder
              title={t('Team')}
              backgroundColor={'var(--main-background-color)'}
            >
              <div className='teamPage'>
                <div className='teamPage__denied'>{t('TeamNoPermission')}</div>
              </div>
            </SubPageInnerContentHolder>
          </SubPageContentHolder>
        </div>
      </PageWrapperMain>
    );
  }

  // ----------------------- render: main view -----------------------
  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={t('Team')}
            subTitle={t('TeamSubTitle')}
            backgroundColor={'var(--main-background-color)'}
            secondaryBtnTitle={canInvite ? t('InviteEmployee') : undefined}
            secondaryBtnIcon={canInvite ? plusSignSVG : undefined}
            secondaryBtnAction={
              canInvite ? () => setInviteOpen(true) : undefined
            }
            secondaryButtonType='white'
            mainBtnTitle={
              canInvite
                ? t('TeamAddWithKey', 'Add employee & generate key now')
                : undefined
            }
            mainBtnIcon={canInvite ? keySVG : undefined}
            mainBtnAction={canInvite ? () => setAddKeyOpen(true) : undefined}
          >
            <div className='teamPage'>
              {error && (
                <div className='teamPage__error'>
                  <span>{error}</span>
                  <button
                    type='button'
                    className='teamPage__error--close'
                    onClick={() => setError(null)}
                    aria-label='Dismiss error'
                  >
                    ×
                  </button>
                </div>
              )}

              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  {/* ----------- Stat tiles ----------- */}
                  <div className='teamStats'>
                    <div className='tstat'>
                      <span
                        className='tstat__v'
                        data-testid='team-stat-members'
                      >
                        {memberCount}
                      </span>
                      <span className='tstat__l'>{t('Employees')}</span>
                    </div>
                    <div className='tstat'>
                      <span
                        className='tstat__v'
                        data-testid='team-stat-approvers'
                      >
                        {approverCount}
                      </span>
                      <span className='tstat__l'>{t('TeamStatApprovers')}</span>
                    </div>
                    <div className='tstat'>
                      <span className='tstat__v'>{groups.length}</span>
                      <span className='tstat__l'>{t('Groups')}</span>
                    </div>
                    <div className='tstat'>
                      <span
                        className={`tstat__v ${
                          pendingCount ? 'tstat__v--amber' : ''
                        }`}
                        data-testid='team-stat-pending'
                      >
                        {pendingCount}
                      </span>
                      <span className='tstat__l'>{t('TeamStatPending')}</span>
                    </div>
                  </div>

                  {/* ----------- Section tabs (Employees / Teams / Clients) ----------- */}
                  <nav className='teamPage__tabs'>
                    <button
                      type='button'
                      className={`teamPage__tab${
                        activeTab === 'employees' ? ' teamPage__tab--active' : ''
                      }`}
                      onClick={() => setActiveTab('employees')}
                    >
                      {t('Employees')}
                    </button>
                    <button
                      type='button'
                      className={`teamPage__tab${
                        activeTab === 'teams' ? ' teamPage__tab--active' : ''
                      }`}
                      onClick={() => setActiveTab('teams')}
                    >
                      {t('Groups')}
                    </button>
                    {canManageClients && (
                      <button
                        type='button'
                        className={`teamPage__tab${
                          activeTab === 'clients' ? ' teamPage__tab--active' : ''
                        }`}
                        onClick={() => setActiveTab('clients')}
                      >
                        {t('TeamClientsHeading')}
                      </button>
                    )}
                    {canViewActivity && (
                      <button
                        type='button'
                        className={`teamPage__tab${
                          activeTab === 'activity' ? ' teamPage__tab--active' : ''
                        }`}
                        onClick={() => setActiveTab('activity')}
                      >
                        {t('TeamActivityTab')}
                      </button>
                    )}
                  </nav>

                  {/* ----------- Members section ----------- */}
                  {activeTab === 'employees' && (
                  <div className='teamPage__section'>
                    <div className='teamPage__sectionHeader'>
                      <h2>{t('Employees')}</h2>
                      <span>
                        {filteredEmployees.length} / {employees.length}
                      </span>
                    </div>

                    <div className='memberControls'>
                      <div className='memberControls__search'>
                        <Icon name='search' size={16} />
                        <input
                          type='text'
                          placeholder={t('SearchEmployeesPlaceholder')}
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                        />
                      </div>
                      <div className='roleFilter'>
                        {roleFilters.map((f) => (
                          <button
                            key={f.value || 'all'}
                            type='button'
                            className={`roleFilter__btn ${
                              roleFilter === f.value
                                ? 'roleFilter__btn--active'
                                : ''
                            }`}
                            onClick={() => setRoleFilter(f.value)}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {filteredEmployees.length === 0 ? (
                      employees.length === 0 ? (
                        <TeachingEmptyState
                          icon={twoUsersSVG}
                          title={t('TeamEmptyTitle')}
                          body={t('TeamEmptyBody')}
                          actionLabel={
                            canInvite ? t('TeamEmptyAction') : undefined
                          }
                          onAction={
                            canInvite ? () => setInviteOpen(true) : undefined
                          }
                        />
                      ) : (
                        <div className='teamPage__empty'>
                          {t('TeamNoMatchingEmployees')}
                        </div>
                      )
                    ) : (
                      <div className='memberList'>
                        {filteredEmployees.map((emp) => {
                          const pending = emp.invitation_accepted_at == null;
                          const inactive = emp.active === false;
                          const locked = !!SCOPE_LOCKED_ROLES[emp.role];
                          const brandN = (emp.assigned_shops ?? []).length;
                          const grps = memberGroups[emp.id] ?? [];
                          // A member at or above the signed-in member's own
                          // role gets no edit, delete or resend control; the
                          // only owner cannot be deleted. A member who may
                          // not manage the team (read-only) gets none at all,
                          // their own row included.
                          const manageable =
                            canInvite &&
                            canManageMember(rankCaller, emp, orgRoles);
                          const deletable =
                            manageable && !isOnlyOwner(emp, employees);
                          return (
                            <div
                              key={emp.id}
                              className='memberRow'
                              data-testid={`team-member-row-${emp.id}`}
                              {...(manageable
                                ? {
                                    role: 'button',
                                    tabIndex: 0,
                                    onClick: () => setAccessTargetId(emp.id),
                                    onKeyDown: (e: React.KeyboardEvent) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setAccessTargetId(emp.id);
                                      }
                                    },
                                  }
                                : { style: { cursor: 'default' } })}
                            >
                              <Avatar name={emp.name} email={emp.email} />
                              <div className='memberRow__id'>
                                <div className='memberRow__nameline'>
                                  <span className='memberRow__name'>
                                    {emp.name || emp.email}
                                  </span>
                                  <RoleBadge
                                    role={emp.role}
                                    label={roleNameByKey[emp.role]}
                                  />
                                  {emp.is_team_lead && (
                                    <span className='groupPill'>
                                      {t('TeamLead')}
                                    </span>
                                  )}
                                  {pending && (
                                    <span className='pendingPill'>
                                      <span className='pendingPill__dot' />
                                      {t('PendingInvite')}
                                    </span>
                                  )}
                                  {inactive && (
                                    <span className='inactivePill'>
                                      {t('Inactive')}
                                    </span>
                                  )}
                                </div>
                                <span className='memberRow__email'>
                                  {emp.email}
                                </span>
                              </div>

                              <div className='memberRow__access'>
                                <div className='accessSum'>
                                  <span className='accessSum__item'>
                                    <Icon name='brand' size={15} />
                                    {locked
                                      ? t('TeamAllBrands')
                                      : brandN === 0
                                      ? t('TeamNoBrandsShort')
                                      : brandN === 1
                                      ? t('TeamOneBrand')
                                      : t('TeamBrandsN', { count: brandN })}
                                  </span>
                                  {grps.length > 0 && (
                                    <div className='accessSum__groups'>
                                      {grps.map((g) => (
                                        <span
                                          key={g.id}
                                          className='groupPill'
                                        >
                                          <span
                                            className='groupPill__dot'
                                            style={{
                                              backgroundColor: groupColor(g.id),
                                            }}
                                          />
                                          {g.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className='memberRow__actions'>
                                {pending && manageable && (
                                  <RoleGate capability='team.invite'>
                                    <button
                                      type='button'
                                      className='ghostBtn'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setResendTarget(emp);
                                      }}
                                    >
                                      <Icon name='send' size={15} />
                                      {t('ResendInvite')}
                                    </button>
                                  </RoleGate>
                                )}
                                {deletable && (
                                  <RoleGate capability='team.delete'>
                                    <button
                                      type='button'
                                      className='ghostBtn ghostBtn--icon ghostBtn--danger'
                                      aria-label={t('Delete')}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteTarget(emp);
                                      }}
                                    >
                                      <Icon name='trash' size={16} />
                                    </button>
                                  </RoleGate>
                                )}
                                {manageable && (
                                  <span
                                    className='ghostBtn ghostBtn--icon'
                                    aria-hidden='true'
                                  >
                                    <Icon name='chevronRight' size={16} />
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  )}

                  {/* ----------- Teams section (API identifier: groups) ----------- */}
                  {activeTab === 'teams' && (
                  <div className='teamPage__section'>
                    <div className='teamPage__sectionHeader'>
                      <div>
                        <h2>{t('Groups')}</h2>
                        <span className='teamPage__sectionSub'>
                          {t('TeamsRoleHelper')}
                        </span>
                      </div>
                      <span>{groups.length}</span>
                    </div>

                    {groups.length === 0 && !canInvite ? (
                      <div className='teamPage__empty'>{t('TeamNoGroups')}</div>
                    ) : (
                      <div className='groupGrid'>
                        {groups.map((g) => {
                          const memberCount =
                            g.members?.length ?? g.member_count ?? 0;
                          const avs = (g.members ?? []).slice(0, 5);
                          return (
                            <div
                              key={g.id}
                              className='gcard'
                              role='button'
                              tabIndex={0}
                              data-testid={`team-card-${g.id}`}
                              onClick={() => openManageGroup(g.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openManageGroup(g.id);
                                }
                              }}
                            >
                              <div className='gcard__head'>
                                <span
                                  className='gcard__dot'
                                  style={{ backgroundColor: groupColor(g.id) }}
                                />
                                <span className='gcard__name'>{g.name}</span>
                                <span className='gcard__count'>
                                  {memberCount}{' '}
                                  {memberCount === 1
                                    ? t('MemberLower')
                                    : t('MembersLower')}
                                </span>
                              </div>
                              <div className='gcard__lead'>
                                {t('Lead')}:{' '}
                                <strong data-testid={`team-card-lead-${g.id}`}>
                                  {teamLeadName(g, employees) || '—'}
                                </strong>
                              </div>
                              <div className='gcard__lead'>
                                {t('GroupRole')}:{' '}
                                <strong>
                                  {g.role ? roleLabel(g.role) : t('NoInheritedRole')}
                                </strong>
                              </div>
                              {avs.length > 0 && (
                                <div className='gcard__avatars'>
                                  {avs.map((m) => (
                                    <Avatar
                                      key={m.id}
                                      name={m.name}
                                      email={m.email}
                                      title={m.name || m.email}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <RoleGate capability='team.invite'>
                          <div
                            className='gcard gcard--add'
                            role='button'
                            tabIndex={0}
                            onClick={openGroupDialog}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openGroupDialog();
                              }
                            }}
                          >
                            <span className='gcardAdd__plus'>+</span>
                            <span className='gcardAdd__label'>
                              {t('NewGroup')}
                            </span>
                          </div>
                        </RoleGate>
                      </div>
                    )}
                  </div>
                  )}

                  {/* ----------- Clients section (whitelabel portal) ----------- */}
                  {/* Relocated here from Reporting (product-owner decision):
                      invite/manage a brand's whitelabel report-portal clients.
                      Gated on the EXISTING `portal.clients.manage` capability —
                      no new role. Invites are scoped to the active brand, so
                      clients are added per brand. */}
                  {activeTab === 'clients' && canManageClients && (
                    <div className='teamPage__section'>
                      <div className='teamPage__sectionHeader'>
                        <div>
                          <h2>{t('TeamClientsHeading')}</h2>
                          <span className='teamPage__sectionSub'>
                            {t('TeamClientsHelper')}
                          </span>
                        </div>
                      </div>
                      <ManageClientsPanel />
                    </div>
                  )}

                  {activeTab === 'activity' && canViewActivity && (
                    <TeamActivityPanel
                      roleLabel={roleLabel}
                      onForbidden={handleActivityForbidden}
                    />
                  )}
                </>
              )}
            </div>

            {/* ----------- Invitation result popup ----------- */}
            <Popup
              styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
              trigger={!!invitationResult}
              setTrigger={() => setInvitationResult(null)}
            >
              {invitationResult && (
                <div className='teamPopup'>
                  <h3 className='teamPopup__title'>{t('InvitationSent')}</h3>
                  <span className='teamPopup__subtitle'>
                    {invitationResult.email_sent
                      ? t('InvitationEmailSent', {
                          email: invitationResult.employee?.email ?? '',
                        })
                      : t('InvitationCopyManually', {
                          email: invitationResult.employee?.email ?? '',
                        })}
                  </span>
                  <div className='teamPopup__inviteLink'>
                    <label className='teamPopup__inviteLink-label'>
                      {t('InviteLink')}
                    </label>
                    <div className='teamPopup__inviteLink-value'>
                      {invitationResult.invite_url}
                    </div>
                    <div className='teamPopup__inviteLink-meta'>
                      {t('LinkExpiresAt', {
                        when: new Date(
                          invitationResult.expires_at
                        ).toLocaleString(),
                      })}
                    </div>
                  </div>
                  <div className='teamPopup__actions'>
                    <MainSquareButton
                      type='button'
                      value={t('CopyInviteLink')}
                      icon={copySVG}
                      buttonType='white'
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        copyInviteUrl();
                      }}
                    />
                    <MainSquareButton
                      type='button'
                      value={t('Done')}
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        setInvitationResult(null);
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>

            {/* ----------- One-time API key popup (add-with-key) ----------- */}
            <Popup
              styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
              trigger={!!keyResult}
              setTrigger={() => setKeyResult(null)}
            >
              {keyResult && (
                <div className='teamPopup'>
                  <h3 className='teamPopup__title'>
                    {t('TeamKeyCreatedTitle', 'API key created')}
                  </h3>
                  <span className='teamPopup__subtitle'>
                    {t('TeamKeyCreatedSubtitle', {
                      email: keyResult.data.email,
                      defaultValue:
                        '{{email}} can now sign in. Copy the key below and hand it over — it is shown once and cannot be retrieved again.',
                    })}
                  </span>
                  <div className='teamPopup__inviteLink'>
                    <label className='teamPopup__inviteLink-label'>
                      {t('TeamKeyLabel', 'API key')}
                    </label>
                    <SecretValue
                      value={keyResult.api_key}
                      className='teamPopup__inviteLink-value'
                    />
                    <div className='teamPopup__inviteLink-meta'>
                      {t(
                        'TeamKeySaveNowWarning',
                        'Save it now — it will not be shown again.'
                      )}
                    </div>
                  </div>
                  {!keyResult.has_active_plan && (
                    <div className='scopeNote scopeNote--warn'>
                      <Icon name='info' size={15} />
                      <span>
                        {t(
                          'TeamKeyNoPlanNote',
                          "The key works, but this member's AI tools stay empty until your organisation has an active plan."
                        )}
                      </span>
                    </div>
                  )}
                  <div className='teamPopup__actions'>
                    <MainSquareButton
                      type='button'
                      value={t('TeamCopyKey', 'Copy key')}
                      icon={copySVG}
                      buttonType='white'
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        copyGeneratedKey();
                      }}
                    />
                    <MainSquareButton
                      type='button'
                      value={t('Done')}
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        setKeyResult(null);
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>

            {/* ----------- Resend confirm popup ----------- */}
            <Popup
              styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
              trigger={!!resendTarget}
              setTrigger={() => !resendSubmitting && setResendTarget(null)}
            >
              {resendTarget && (
                <div className='teamPopup'>
                  <h3 className='teamPopup__title'>{t('ResendInvitation')}</h3>
                  <p className='teamPopup__confirmBody'>
                    {t('ResendInvitationConfirm', {
                      email: resendTarget.email,
                    })}
                  </p>
                  <div className='teamPopup__actions'>
                    <MainSquareButton
                      type='button'
                      value={t('Cancel')}
                      buttonType='white'
                      normal={true}
                      disabled={resendSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        setResendTarget(null);
                      }}
                    />
                    <MainSquareButton
                      type='button'
                      value={t('Resend')}
                      icon={sendSVG}
                      normal={true}
                      loading={resendSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        submitResend();
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>

            {/* ----------- Delete confirm popup ----------- */}
            <Popup
              styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
              trigger={!!deleteTarget}
              setTrigger={() => !deleteSubmitting && setDeleteTarget(null)}
            >
              {deleteTarget && (
                <div className='teamPopup'>
                  <h3 className='teamPopup__title'>{t('DeleteEmployee')}</h3>
                  <p className='teamPopup__confirmBody'>
                    {t('DeleteEmployeeConfirm', { email: deleteTarget.email })}
                  </p>
                  <div className='teamPopup__actions'>
                    <MainSquareButton
                      type='button'
                      value={t('Cancel')}
                      buttonType='white'
                      normal={true}
                      disabled={deleteSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteTarget(null);
                      }}
                    />
                    <MainSquareButton
                      type='button'
                      value={t('Delete')}
                      icon={trashSVG}
                      buttonType='danger'
                      normal={true}
                      loading={deleteSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        submitDelete();
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>

            {/* ----------- Create group popup ----------- */}
            <Popup
              styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
              trigger={groupDialogOpen}
              setTrigger={() => !groupSubmitting && setGroupDialogOpen(false)}
            >
              <form className='teamPopup' onSubmit={submitCreateGroup}>
                <h3 className='teamPopup__title'>{t('CreateGroup')}</h3>
                <span className='teamPopup__subtitle'>
                  {t('CreateGroupSubtitle')}
                </span>
                <div className='teamPopup__form'>
                  <AINormalInput
                    id='team-group-name'
                    value={groupName}
                    placeholder={t('GroupName')}
                    required={true}
                    changeListeners={[
                      (e) => {
                        setGroupError(null);
                        setGroupName((e.target as HTMLInputElement).value);
                      },
                    ]}
                  />
                  {/* A team cannot exist without a lead: the first entry is
                      only a prompt and cannot be picked. */}
                  <AISelectInput
                    id='team-group-lead'
                    defaultValue={groupLead}
                    setSelect={changeGroupField(groupLead, setGroupLead)}
                    selectOptionArr={[
                      {
                        OptionValue: '',
                        OptionName: t('TeamLeadChoose'),
                        OptionDisabled: true,
                      },
                      ...employees.map((emp) => ({
                        OptionValue: emp.id,
                        OptionName: `${emp.name || emp.email} (${emp.email})`,
                      })),
                    ]}
                    placeholder={t('TeamLeadRequired')}
                  />
                  <AISelectInput
                    id='team-group-role'
                    defaultValue={groupRole}
                    setSelect={changeGroupField(groupRole, setGroupRole)}
                    selectOptionArr={[
                      { OptionValue: '', OptionName: t('NoInheritedRole') },
                      ...roleOptions.map((r) => ({
                        OptionValue: r.key,
                        OptionName: r.name,
                      })),
                    ]}
                    placeholder={t('GroupRoleOptional')}
                  />
                  <p className='scopeFoot'>{t('TeamGroupRoleHint')}</p>
                </div>
                {groupError && (
                  <p className='teamPopup__error' role='alert'>
                    {groupError}
                  </p>
                )}
                <div className='teamPopup__actions'>
                  <MainSquareButton
                    type='button'
                    value={t('Cancel')}
                    buttonType='white'
                    normal={true}
                    disabled={groupSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      setGroupDialogOpen(false);
                    }}
                  />
                  <MainSquareButton
                    type='submit'
                    value={t('Create')}
                    normal={true}
                    loading={groupSubmitting}
                    disabled={!teamFormReady(groupName, groupLead)}
                  />
                </div>
              </form>
            </Popup>

            {/* ----------- Manage group popup ----------- */}
            <Popup
              styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
              trigger={!!managedGroup}
              setTrigger={() =>
                !groupDeleteSubmitting && setGroupManageId(null)
              }
            >
              {managedGroup && groupDeleteConfirm && (
                <div className='teamPopup' data-testid='team-delete-confirm'>
                  <h3 className='teamPopup__title'>{t('TeamGroupDelete')}</h3>
                  <p className='teamPopup__confirmBody'>
                    {t('TeamGroupDeleteConfirm', { name: managedGroup.name })}
                  </p>
                  {groupDeleteError && (
                    <p className='teamPopup__error' role='alert'>
                      {groupDeleteError}
                    </p>
                  )}
                  <div className='teamPopup__actions'>
                    <MainSquareButton
                      type='button'
                      value={t('Cancel')}
                      buttonType='white'
                      normal={true}
                      disabled={groupDeleteSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        setGroupDeleteConfirm(false);
                        setGroupDeleteError(null);
                      }}
                    />
                    <MainSquareButton
                      type='button'
                      value={t('TeamGroupDelete')}
                      icon={trashSVG}
                      buttonType='danger'
                      normal={true}
                      loading={groupDeleteSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        submitDeleteGroup();
                      }}
                    />
                  </div>
                </div>
              )}
              {managedGroup && !groupDeleteConfirm && (
                <div className='teamPopup'>
                  <h3 className='teamPopup__title'>{managedGroup.name}</h3>
                  <span
                    className='teamPopup__subtitle'
                    data-testid='team-manage-summary'
                  >
                    {t('Lead')}: {teamLeadName(managedGroup, employees) || '—'}{' '}
                    ·{' '}
                    {managedGroup.members?.length ??
                      managedGroup.member_count ??
                      0}{' '}
                    {(managedGroup.members?.length ??
                      managedGroup.member_count ??
                      0) === 1
                      ? t('MemberLower')
                      : t('MembersLower')}
                  </span>

                  {/* Brand-visibility lead scope for this team's members. This
                      drives which brands they can see — NOT what they can do
                      (that comes from each member's Role). */}
                  <RoleGate capability='team.invite'>
                    <div className='teamPopup__form'>
                      <AISelectInput
                        key={`grole-${managedGroup.id}-${
                          managedGroup.role ?? ''
                        }`}
                        id='team-group-role-edit'
                        defaultValue={managedGroup.role ?? ''}
                        setSelect={(val: string) =>
                          handleUpdateGroupRole(managedGroup, val)
                        }
                        selectOptionArr={[
                          { OptionValue: '', OptionName: t('NoInheritedRole') },
                          ...roleOptions.map((r) => ({
                            OptionValue: r.key,
                            OptionName: r.name,
                          })),
                        ]}
                        placeholder={t('GroupRoleOptional')}
                      />
                      <p className='scopeFoot'>{t('TeamGroupRoleHint')}</p>
                    </div>
                  </RoleGate>

                  {managedGroup.members && managedGroup.members.length > 0 ? (
                    <div className='teamPopup__members'>
                      {managedGroup.members.map((m) => (
                        <div className='teamPopup__member' key={m.id}>
                          <div className='teamPopup__member-info'>
                            <strong>{m.name || m.email}</strong>
                            <span>{m.email}</span>
                          </div>
                          <RoleGate capability='team.delete'>
                            <MainSquareButton
                              type='button'
                              value={t('Remove')}
                              buttonType='danger'
                              normal={true}
                              onClick={(e) => {
                                e.preventDefault();
                                handleRemoveMember(managedGroup, m.id);
                              }}
                            />
                          </RoleGate>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='teamPopup__emptyMembers'>
                      {t('TeamGroupNoMembers')}
                    </div>
                  )}

                  <RoleGate capability='team.invite'>
                    <form className='teamPopup__form' onSubmit={submitAddMember}>
                      <AISelectInput
                        key={managedGroup.members?.length ?? 0}
                        id='team-add-member'
                        defaultValue={addMemberEmployeeId}
                        setSelect={setAddMemberEmployeeId}
                        selectOptionArr={[
                          {
                            OptionValue: '',
                            OptionName: t('SelectEmployee'),
                          },
                          ...employees
                            .filter(
                              (e) =>
                                !managedGroup.members?.some(
                                  (m) => m.id === e.id
                                )
                            )
                            .map((emp) => ({
                              OptionValue: emp.id,
                              OptionName: `${emp.name || emp.email} (${
                                emp.email
                              })`,
                            })),
                        ]}
                        placeholder={t('Employee')}
                      />
                      <div className='teamPopup__actions'>
                        <MainSquareButton
                          type='submit'
                          value={t('AddMember')}
                          icon={plusSignSVG}
                          normal={true}
                          loading={addMemberSubmitting}
                          disabled={!addMemberEmployeeId}
                        />
                      </div>
                    </form>
                  </RoleGate>

                  {groupManageError && (
                    <p className='teamPopup__error' role='alert'>
                      {groupManageError}
                    </p>
                  )}
                  <div className='teamPopup__actions'>
                    {/* Owner, or a manager for a team whose role is below
                        their own; the API decides in the end. */}
                    {canDeleteTeam(role, managedGroup, orgRoles) && (
                      <MainSquareButton
                        type='button'
                        value={t('TeamGroupDelete')}
                        icon={trashSVG}
                        buttonType='danger'
                        normal={true}
                        onClick={(e) => {
                          e.preventDefault();
                          setGroupDeleteError(null);
                          setGroupDeleteConfirm(true);
                        }}
                      />
                    )}
                    <MainSquareButton
                      type='button'
                      value={t('Done')}
                      buttonType='white'
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        setGroupManageId(null);
                      }}
                    />
                  </div>
                </div>
              )}
            </Popup>

            {/* ----------- Slide-over drawers ----------- */}
            {accessTarget && (
              <AccessDrawer
                key={accessTarget.id}
                member={accessTarget}
                accessibleShops={accessibleShops}
                roleOptions={accessRoleOptions}
                allPlatforms={allPlatforms}
                saving={accessSaving}
                onlyOwner={accessTargetOnlyOwner}
                onClose={() => setAccessTargetId(null)}
                onSave={saveAccess}
              />
            )}
            {inviteOpen && (
              <InviteDrawer
                accessibleShops={accessibleShops}
                roleOptions={givableRoleOptions}
                allPlatforms={allPlatforms}
                submitting={inviteSubmitting}
                pending={invitePending}
                refusal={inviteRefusal}
                onEmailChange={() => setInviteRefusal(null)}
                onClose={() => {
                  setInviteOpen(false);
                  setInvitePending(null);
                  setInviteRefusal(null);
                }}
                onInvite={submitInviteDrawer}
                onResendPending={resendPendingInvite}
              />
            )}
            {addKeyOpen && (
              <AddKeyDrawer
                accessibleShops={accessibleShops}
                roleOptions={givableRoleOptions}
                allPlatforms={allPlatforms}
                submitting={addKeySubmitting}
                refusal={addKeyRefusal}
                onEmailChange={() => setAddKeyRefusal(null)}
                onClose={() => {
                  setAddKeyOpen(false);
                  setAddKeyRefusal(null);
                }}
                onCreate={submitAddKeyDrawer}
              />
            )}
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
};

export default TeamPage;
